import React, { useState } from "react";
import { MapPin, Gift, Flame, BookOpen, Clock } from "lucide-react";
import MenuModal from "./MenuModal";

// Logo oficial de WhatsApp (SVG inline) — verde #25D366
function WhatsAppIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <path
        fill="#25D366"
        d="M16.003 3.2C9.0944 3.2 3.4995 8.795 3.4995 15.703c0 2.207.586 4.299 1.612 6.106L3.2 28.8l7.166-1.864a12.434 12.434 0 0 0 5.637 1.346h.005c6.907 0 12.503-5.595 12.503-12.503 0-6.908-5.596-12.578-12.508-12.578Zm0 22.79h-.005a10.36 10.36 0 0 1-5.272-1.443l-.378-.224-4.247 1.104 1.134-4.135-.246-.391a10.354 10.354 0 0 1-1.587-5.508c0-5.737 4.673-10.408 10.41-10.408 2.78 0 5.392 1.083 7.357 3.05a10.342 10.342 0 0 1 3.043 7.36c-.001 5.737-4.673 10.595-10.41 10.595Zm5.706-7.741c-.312-.157-1.852-.913-2.139-1.017-.286-.105-.494-.157-.703.157-.208.314-.806 1.017-.989 1.226-.182.21-.364.235-.677.078-.312-.157-1.32-.486-2.514-1.55-.93-.83-1.557-1.853-1.74-2.166-.182-.314-.02-.483.137-.64.141-.14.313-.366.47-.549.156-.183.208-.314.313-.523.105-.21.052-.392-.026-.55-.078-.156-.703-1.695-.963-2.32-.254-.61-.512-.527-.703-.537a13.6 13.6 0 0 0-.6-.012c-.208 0-.547.078-.832.392-.286.314-1.092 1.067-1.092 2.601s1.118 3.018 1.274 3.227c.156.21 2.201 3.361 5.336 4.713.745.321 1.327.513 1.78.657.748.238 1.428.205 1.966.124.6-.09 1.852-.757 2.114-1.488.26-.732.26-1.36.182-1.488-.078-.13-.286-.21-.598-.366Z"
      />
    </svg>
  );
}

const formatWhatsApp = (w) => {
  if (!w) return "";
  const d = String(w).replace(/\D/g, "");
  if (d.length === 10) return `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}`;
  if (d.length === 11) return `${d.slice(0, 3)} ${d.slice(3, 7)} ${d.slice(7)}`;
  if (d.length === 12) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 8)} ${d.slice(8)}`;
  return w;
};

export default function AdCard({ business, ad, index = 0 }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const discount = ad?.discount_text || business?.giro;
  const wa = business?.whatsapp || "";
  const waDigits = wa.replace(/\D/g, "");
  const whatsappUrl = wa
    ? `https://wa.me/52${waDigits}?text=${encodeURIComponent(
        `Hola, vi su anuncio en OFERTAS y me interesa la promoción "${discount}"`
      )}`
    : null;
  const loyalty = business?.loyalty_enabled;
  const dv = Number(ad?.discount_value || 0);
  const isHotDeal = dv >= 40;

  return (
    <article
      className="ad-card group bg-white rounded-2xl overflow-hidden border border-border hover:border-[#C4B5FD] hover:shadow-lg transition-all duration-300 flex flex-col fade-in-up"
      style={{ animationDelay: `${Math.min(index * 0.04, 0.4)}s` }}
      data-testid={`ad-card-${business?.id}`}
    >
      <div className="relative aspect-square bg-[#F4F4F5] overflow-hidden">
        {ad?.image_base64 ? (
          <button
            onClick={() => setShowImage(true)}
            className="w-full h-full block cursor-zoom-in focus:outline-none"
            aria-label="Ver anuncio ampliado"
            data-testid={`ad-image-btn-${business?.id}`}
          >
            <img
              src={ad.image_base64}
              alt={business.business_name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </button>
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground text-sm p-6 text-center">
            Sin anuncio aún
          </div>
        )}
        {business?.logo_base64 && (
          <div className="absolute bottom-3 right-3 w-14 h-14 rounded-full bg-white p-1 border border-border shadow-sm opacity-50 group-hover:opacity-100 transition-opacity duration-300">
            <img src={business.logo_base64} alt="logo" className="w-full h-full rounded-full object-cover" />
          </div>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        {(ad?.discount_text || isHotDeal) && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {ad?.discount_text && (
              <span className="discount-stamp px-3 py-1 rounded-md text-sm font-bold uppercase tracking-wide" data-testid="discount-badge">
                {ad.discount_text}
              </span>
            )}
            {isHotDeal && (
              <span
                className="bg-gradient-to-r from-rose-500 to-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full flex items-center gap-1"
                data-testid={`hot-deal-badge-${business?.id}`}
                title="Una de las mejores promociones del directorio"
              >
                <Flame className="w-3 h-3" /> Top oferta
              </span>
            )}
          </div>
        )}
        <div className="overline text-muted-foreground mb-1">{business?.giro}</div>
        <h3 className="font-display text-2xl leading-tight line-clamp-2 tracking-tight" data-testid={`business-name-${business?.id}`}>
          {business?.business_name}
        </h3>
        {ad?.description && (
          <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{ad.description}</p>
        )}

        {loyalty && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 self-start bg-gradient-to-r from-[#EDE9FE] to-[#F5F3FF] border border-[#C4B5FD] text-[#5B21B6] text-[11px] px-2.5 py-1 rounded-full font-semibold"
            data-testid={`loyalty-badge-${business?.id}`}
            title={
              business.loyalty_points_per_purchase && business.loyalty_threshold && business.loyalty_reward
                ? `${business.loyalty_points_per_purchase} pts ${business.loyalty_purchase_label || "por compra"} · Con ${business.loyalty_threshold} pts: ${business.loyalty_reward}`
                : "Este comercio ofrece puntos por tus compras"
            }
          >
            <Gift className="w-3 h-3" />
            Acumulas puntos
          </div>
        )}

        <div className="mt-auto pt-3">
          <div className="flex items-start gap-2 text-base text-zinc-700">
            <MapPin className="w-5 h-5 mt-0.5 shrink-0 text-[#7C3AED]" />
            <span className="line-clamp-2 font-medium">{business?.address} · CP {business?.postal_code}</span>
          </div>

          {business?.horario && (
            <div className="mt-2.5 flex items-center gap-2 text-sm text-zinc-600" data-testid={`horario-${business?.id}`}>
              <Clock className="w-4 h-4 shrink-0 text-[#7C3AED]" />
              <span>{business.horario}</span>
            </div>
          )}

          {wa && (
            <div className="mt-2.5 flex items-center gap-2 text-base text-zinc-800" data-testid={`whatsapp-display-${business?.id}`}>
              <WhatsAppIcon className="w-5 h-5 shrink-0" />
              <span className="font-mono font-semibold tracking-wide">{formatWhatsApp(wa)}</span>
            </div>
          )}

          {whatsappUrl && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1FB857] text-white text-sm font-semibold py-2.5 transition-colors shadow-sm"
              data-testid={`whatsapp-btn-${business?.id}`}
            >
              <WhatsAppIcon className="w-5 h-5 bg-white rounded-full p-0.5" /> Contactar por WhatsApp
            </a>
          )}

          {business?.has_menu && (
            <button
              onClick={() => setShowMenu(true)}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#C4B5FD] text-[#5B21B6] text-sm font-semibold py-2.5 hover:bg-[#F5F3FF] transition-colors"
              data-testid={`menu-btn-${business?.id}`}
            >
              <BookOpen className="w-4 h-4" /> Ver menú
            </button>
          )}
        </div>
      </div>

      {showMenu && (
        <MenuModal
          businessId={business?.id}
          businessName={business?.business_name}
          onClose={() => setShowMenu(false)}
        />
      )}

      {showImage && ad?.image_base64 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowImage(false)}
          data-testid={`ad-image-modal-${business?.id}`}
        >
          <div
            className="relative max-w-lg w-full max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={ad.image_base64}
              alt={business.business_name}
              className="w-full h-auto block"
            />
            <button
              onClick={() => setShowImage(false)}
              className="absolute top-3 right-3 bg-black/50 hover:bg-black/70 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg leading-none transition-colors"
              aria-label="Cerrar"
            >
              ×
            </button>
            {business?.business_name && (
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-4 py-3">
                <p className="text-white font-semibold text-sm">{business.business_name}</p>
                {ad?.discount_text && (
                  <p className="text-white/80 text-xs mt-0.5">{ad.discount_text}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
